import { computed, getCurrentInstance } from "@vue/composition-api";
import { User } from "@scrile/api-provider/dist/api/UserProvider";
import { UserVerificationStatus } from "@scrile/api-provider/dist/api/UserVerificationProvider";
import useRole from "@/hooks/useRole";
import store from "@/store";
import { MUTATION_TYPES } from "@/store/const";
import { StateRoot } from "@/store/state";
import providers from "@/lib/api";
import { allowAccess as allowAccessHelper } from "@/lib/loginHelpers";

export default function useAuthUser() {
  const vm = getCurrentInstance();
  const authUser = computed<StateRoot["user"]>(() => (vm ? (vm.$store.state as StateRoot).user : null));
  const userVerification = computed<StateRoot["userVerification"]>(() =>
    vm ? (vm.$store.state as StateRoot).userVerification : null
  );
  const userVerificationStatus = computed<UserVerificationStatus>(
    () => userVerification.value?.status ?? UserVerificationStatus.UNVERIFIED
  );
  const userVerificationUpgradeStatus = computed<UserVerificationStatus>(
    () => userVerification.value?.upgradeStatus ?? UserVerificationStatus.UNVERIFIED
  );
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const authUserStrict = computed(() => authUser.value!);
  const socialLogin = computed<StateRoot["socialLogin"]>(() =>
    vm ? (vm.$store.state as StateRoot).socialLogin : false
  );
  const authorities = computed<StateRoot["authorities"]>(() => (vm ? (vm.$store.state as StateRoot).authorities : []));
  const allowAccess = computed(() => allowAccessHelper(authUser.value));

  const { isAdmin, isMember, isCreator, isFullCreator, isTalentAgent, isAnyCreator } = useRole(authUser);

  const updateUser = (user: User, authorities: string[] = vm?.$store.state.authorities ?? []) => {
    store.commit(MUTATION_TYPES.SET_LOGIN_DATA, { user, authorities });
  };

  const setVerificationStatus = async (status: boolean) => {
    const { authorities } = await providers.UserProvider.getMyAuthorities();
    updateUser(authUserStrict.value, authorities);
    store.commit(MUTATION_TYPES.SET_VERIFICATION_STATUS, status);
  };

  const setSocialLoginStatus = (status: boolean) => {
    store.commit(MUTATION_TYPES.SET_SOCIAL_LOGIN, status);
  };

  return {
    authUser,
    userVerificationStatus,
    userVerificationUpgradeStatus,
    userVerification,
    authUserStrict,
    socialLogin,
    authorities,
    allowAccess,
    isAdmin,
    isMember,
    isCreator,
    isFullCreator,
    isAnyCreator,
    isTalentAgent,
    updateUser,
    setVerificationStatus,
    setSocialLoginStatus,
  };
}
